import SaveIcon from "@mui/icons-material/Save";
import { keyframes, styled } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { MRT_PaginationState as PaginationState } from "material-react-table";
import LoadingButton from "_shared/_views/loading/loading-button";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import {
  getFeeScheduleTeamMembers,
  withFeeScheduleStaffingSeated,
} from "_shared/fee-schedules/categories/fee-schedule-team-members";
import useReconcileFeeScheduleStaffing from "_shared/fee-schedules/categories/use-reconcile-fee-schedule-staffing";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import FeeScheduleTag from "_shared/fee-schedules/tags/fee-schedule-tag";
import Guid from "_shared/guids/guid";
import _ from "lodash";
import { enqueueSnackbar } from "notistack";
import React, { useMemo, useState } from "react";
import { useSession } from "users/__session/session-context";
import { FeeScheduleCategoryMaskedCurrency, Masked } from "work/masked-fields";
import {
  useCreateFeeScheduleTemplateMutation,
  useGetFeeScheduleTemplatesQuery,
} from "work/fee-schedule-templates/_api/fee-schedule-template-api";
import FeeScheduleTemplateQuery from "work/fee-schedule-templates/_api/queries/contracts/fee-schedule-template-query";
import { FeeScheduleTemplateFilterParameters } from "work/fee-schedule-templates/_api/queries/contracts/filtering/fee-schedule-template-filter-parameters";
import { FeeScheduleTemplateOrderField } from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-field";
import {
  FeeScheduleTemplateOrderParameters,
} from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-parameters";
import { isFeeScheduleTemplateLoadIssue } from "work/fee-schedule-templates/_store/fee-schedule-templates-redux-slice";
import { FeeScheduleTagChip } from "work/fee-schedule-templates/_views/_shared/embedded-containers";
import FeeScheduleTemplatesTable from "work/fee-schedule-templates/_views/tables/fee-schedule-templates-table";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import ProposalIssues, { ProposalIssueLevel } from "work/proposals/issues/proposal-issues";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { type TemplateOwnershipScope } from "_shared/_views/templates/template-ownership-filter";
import TemplateSelector, { type TemplateSelectorRow } from "_shared/_views/templates/template-selector";
import ResizableTemplatePanel from "work/proposals/_views/_shared/resizable-template-panel";
import {
  NO_TEMPLATE_CONTRIBUTIONS,
  TemplateContributions,
  withTemplateDeselected,
  withTemplateSelected,
} from "work/proposals/_views/_shared/template-contributions";
import { MaskedProposalField, maskedProposalFieldNotice } from "work/proposals/_views/_shared/masked-field-notice";
import {
  paneIsCompact,
} from "work/_views/work-pane-layout";
import { useWorkPaneIsCompact } from "work/_views/use-work-pane-size";

const pulseBorder = keyframes`
    0%, 100% {
        box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
        border-color: rgb(21, 101, 192);
        box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
    }
`;
const ListContainer = styled(Grid)(({ theme }) => ({
  display: "flex",
  flex: "1 1 auto",
  flexWrap: "nowrap",
  height: "100%",
  minHeight: 0,
  minWidth: 0,
  overflow: "hidden",
  width: "100%",
  [paneIsCompact(theme)]: {
    flexDirection: "column",
  },
}));
const FeeScheduleContent = styled(Grid)(() => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minHeight: 0,
  minWidth: 0,
  overflow: "hidden",
}));
/** The template's own name in the panel's Name cell, beside its tag chips. */
const TemplateRowName = styled(Typography)(() => ({
  flex: "0 1 auto",
  fontSize: "1em",
  fontWeight: 500,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const CategoryRowText = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
}));
const CategoryRowDetail = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.75rem",
  lineHeight: 1.3,
  minWidth: 0,
  overflow: "hidden",
}));
const CategorySelection = styled(FeeScheduleTemplatesTable)(({ theme }) => ({
  height: "100%",
  flex: 1,
  minHeight: 0,
  marginLeft: theme.spacing(0),
  minWidth: 0,
  overflow: "hidden",
}));
/** What `pulseBorder` grows its ring to at the peak of the animation, outside the border box. */
const ISSUE_PULSE_SPREAD = 3;
/**
 * The tab's issues, and the box the traverser pulses when one of them is the field you are on.
 *
 * `maxWidth` takes this box's own offsets out by hand. A percentage resolves against the parent's
 * content box and knows nothing about either the 16px this box is pushed right by while it is
 * active or the ring the pulse draws outside its border box — so at full width it reached past the
 * parent's right edge, and the parent's `overflow: hidden` cut the ring and the rounded corner off
 * on that side while leaving the other three intact.
 *
 * A step down from the `body1` the issues are written in, at every width. Two of them at 16px wrap
 * to four lines across this column and take the height out of the table underneath.
 */
const IssueContainer = styled("div", { shouldForwardProp: (prop) => prop !== "active" })<{ active: boolean }>(
  ({ theme, active }) => ({
    [paneIsCompact(theme)]: {
      margin: theme.spacing(0.5),
      marginBottom: theme.spacing(1),
    },
    alignSelf: "flex-start",
    animation: active ? `${pulseBorder} 1.5s ease-in-out infinite` : "none",
    boxSizing: "border-box",
    marginBottom: theme.spacing(1),
    marginLeft: theme.spacing(active ? 2 : 0),
    marginTop: active ? theme.spacing(1) : "initial",
    maxWidth: active
      ? `calc(100% - ${theme.spacing(2)} - ${ISSUE_PULSE_SPREAD * 2}px)`
      : "100%",
    padding: active ? theme.spacing(1) : "initial",
    paddingLeft: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    width: "fit-content",
    "& .MuiTypography-root": {
      fontSize: "0.875rem",
    },
  }),
);
// The tooltip's span wrapper sits directly in the table toolbar's button row, so it has to behave
// like the plain buttons beside it: content-width when the row is horizontal, full-width when the
// row stacks vertically at narrower breakpoints.
const CreateTemplateActionWrapper = styled("span")(() => ({
  display: "flex",
}));
const CreateTemplateAction = styled(LoadingButton)(() => ({
  flex: 1,
}));

type SelectFeeScheduleProps = {
  activeReviewField?: ProposalField;
  proposalBuilder: ProposalBuilder;
  issues?: ProposalIssues;
  showPendingChangeHighlights?: boolean;
  isDrawerOpen: boolean;
  negotiationId?: string;
  viewerScope?: string;
  disableEditing?: boolean;
  fieldLockTooltip?: string;
  onProposalBuilderUpdated: (updatedProposalBuilder: ProposalBuilder) => void;
  onCreateCategoryClicked: () => void;
  onAddExistingCategoryClicked: () => void;
  onEditCategory: (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => void;
};
type FeeScheduleTemplateCategory = FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency;

export default function FeeScheduleTab(props: Readonly<SelectFeeScheduleProps>) {
  const INTERFACE_NAME = "DraftProposalFeeScheduleTab";

  const {
    activeReviewField,
    proposalBuilder,
    showPendingChangeHighlights,
    isDrawerOpen,
    disableEditing,
    fieldLockTooltip,
    negotiationId,
    viewerScope,
    onProposalBuilderUpdated,
    onCreateCategoryClicked,
    onAddExistingCategoryClicked,
    onEditCategory,
  } = props;

  const isMdDownDisplay = useWorkPaneIsCompact();
  const session = useSession();
  const userId = session.user!.id;
  const userLegalEntityId = session.user!.legalEntityId;

  const [templateOwnershipScope, setTemplateOwnershipScope] = useState<TemplateOwnershipScope>("all");
  const [templateSearch, setTemplateSearch] = useState("");
  const [templatePagination, setTemplatePagination] = useState<PaginationState>(INITIAL_TEMPLATE_PAGINATION);
  const [templateContributions, setTemplateContributions] =
    useState<TemplateContributions>(NO_TEMPLATE_CONTRIBUTIONS);
  const [isCreateTemplateDialogOpen, setIsCreateTemplateDialogOpen] = useState(false);
  const [createTemplateName, setCreateTemplateName] = useState("");
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const selectedCategories = Masked.isPresentNotMasked(proposalBuilder?.currentSpec?.workAgreement?.feeSchedule)
    ? proposalBuilder.currentSpec.workAgreement.feeSchedule
    : [];
  const selectedTemplateIds = session.context?.viewingAsVendor
    ? proposalBuilder.currentSpec.workAgreement.vendorFeeScheduleTemplateIds
    : proposalBuilder.currentSpec.workAgreement.clientFeeScheduleTemplateIds;
  // Who this draft can already bill. A template may name anyone in the firm, so applying one asks
  // what to do about anybody missing from here. See `useReconcileFeeScheduleStaffing`.
  const currentVendorTeam = Masked.isPresentNotMasked(proposalBuilder?.currentSpec?.workAgreement?.vendorTeam)
    ? proposalBuilder.currentSpec.workAgreement.vendorTeam
    : undefined;
  const vendorTeamMembers = getFeeScheduleTeamMembers(currentVendorTeam);
  // An empty roster is not the same as an unreadable one, and only one of them can be added to.
  const vendorTeamIsMasked = Masked.isMasked(proposalBuilder?.currentSpec?.workAgreement?.vendorTeam);
  const reconcileStaffing = useReconcileFeeScheduleStaffing();
  const selectedTemplateCount = selectedTemplateIds?.length ?? 0;
  const pendingChangeFields = showPendingChangeHighlights
    ? proposalBuilder.sessionHistory?.flatMap((change) => change.field ? [change.field] : []) ?? []
    : [];

  const issues = React.useMemo(
    () =>
      props.issues?.entries.filter((issue) =>
        [ProposalField.FeeSchedule, ProposalField.FeeScheduleCategory(issue.field.id ?? Guid.EMPTY)].some((field) =>
          field.isEqualTo(issue.field),
        ),
      ),
    [props.issues?.entries],
  );

  const templateQueryParams = useMemo(() => {
    const paginationParameters = PaginationParameters.create(templatePagination);
    const filterParameters = new FeeScheduleTemplateFilterParameters({
      entityId: userLegalEntityId,
      creatorUserId: templateOwnershipScope === "own" ? userId : undefined,
    });
    const orderParameters = new FeeScheduleTemplateOrderParameters({
      orderBy: FeeScheduleTemplateOrderField.Name,
      orderDir: OrderDirection.Descending,
    });
    return new FeeScheduleTemplateQuery({
      filterParameters,
      orderParameters,
      paginationParameters,
    });
  }, [templateOwnershipScope, templatePagination, userId, userLegalEntityId]);

  const templatesQueryArgs = useMemo(
    () => ({ query: templateQueryParams, interfaceName: INTERFACE_NAME }),
    [templateQueryParams],
  );
  const templatesQuery = useGetFeeScheduleTemplatesQuery(templatesQueryArgs);
  const [createFeeScheduleTemplate] = useCreateFeeScheduleTemplateMutation();
  const paginatedTemplatesJSON = templatesQuery.data;
  const templateFeeSchedules = useMemo(() => {
    if (!templatesQuery.isSuccess) return [];
    return FeeScheduleTemplate.fromJSONArray(paginatedTemplatesJSON?.data ?? []);
  }, [paginatedTemplatesJSON?.data, templatesQuery.isSuccess]);

  async function handleTemplateSelectionToggled(template: FeeScheduleTemplate) {
    let updatedSelectedTemplateIds = session.context?.viewingAsVendor
      ? [...(proposalBuilder.currentSpec.workAgreement.vendorFeeScheduleTemplateIds ?? [])]
      : [...(proposalBuilder.currentSpec.workAgreement.clientFeeScheduleTemplateIds ?? [])];
    try {
      if (!template.id) throw new Error("No template selected");

      let updatedProposalBuilder = proposalBuilder;
      if (updatedSelectedTemplateIds.some((id) => id.isEqualTo(template.id))) {
        updatedSelectedTemplateIds = updatedSelectedTemplateIds.filter((id) => !id.isEqualTo(template.id));
        const { contributions, itemKeysToRemove: categoryKeysToRemove } = withTemplateDeselected({
          contributions: templateContributions,
          templateId: template.id,
          alsoSuppliedItemKeys: getCategoryKeysOfLoadedTemplates(updatedSelectedTemplateIds),
        });
        if (categoryKeysToRemove.size) {
          updatedProposalBuilder = proposalBuilder.setFeeSchedule(
            selectedCategories.filter((category) => !categoryKeysToRemove.has(getCategoryKey(category))),
          );
        }
        setTemplateContributions(contributions);
      } else {
        updatedSelectedTemplateIds.push(template.id);
        // The template's rates say who they are for. Anyone it names who is not on this draft's
        // vendor team is put to the user before anything is applied (#5237).
        const staffing = await reconcileStaffing({
          categories: template.categories,
          teamMembers: vendorTeamMembers,
          teamIsMasked: vendorTeamIsMasked,
        });
        if (!staffing.applied) return;
        if (staffing.membersToAddToTeam.length > 0) {
          updatedProposalBuilder = updatedProposalBuilder.setVendorTeam(
            withFeeScheduleStaffingSeated(currentVendorTeam ?? new VendorTeam({ members: [] }), staffing.membersToAddToTeam),
          );
        }
        const categories = _.uniqWith(
          [...selectedCategories, ...staffing.categories],
          (a, b) => a.name?.isEqualTo(b.name) ?? false,
        );
        const categoryKeysAlreadySelected = new Set(selectedCategories.map(getCategoryKey));
        const templateCategoryKeys = template.categories.map(getCategoryKey);
        setTemplateContributions(
          withTemplateSelected({
            contributions: templateContributions,
            templateId: template.id,
            suppliedItemKeys: templateCategoryKeys,
            addedItemKeys: templateCategoryKeys.filter(
              (categoryKey) => !categoryKeysAlreadySelected.has(categoryKey),
            ),
          }),
        );
        // Chained, not re-derived from the `proposalBuilder` prop: the vendor team seated a few
        // lines above lives on `updatedProposalBuilder`, and starting again from the prop here
        // threw that away, so "add them to the team" added nobody.
        updatedProposalBuilder = updatedProposalBuilder.setFeeSchedule(categories);
      }
      updatedProposalBuilder = session.context?.viewingAsVendor
        ? updatedProposalBuilder.setVendorFeeScheduleTemplateIds(updatedSelectedTemplateIds)
        : updatedProposalBuilder.setClientFeeScheduleTemplateIds(updatedSelectedTemplateIds);

      onProposalBuilderUpdated(updatedProposalBuilder);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to apply template. Please try again", {
        variant: "error",
      });
    }
  }

  // Supplements the recorded contributions for templates that were already checked when the tab
  // mounted, which have no record of their own. `templateFeeSchedules` is one ownership-filtered
  // page, so this can only ever KEEP a category a still-selected template supplies, never take one
  // away: what it cannot see is covered by the contribution records instead.
  function getCategoryKeysOfLoadedTemplates(templateIds: Guid[]): string[] {
    return templateFeeSchedules
      .filter((loadedTemplate) => templateIds.some((id) => id.isEqualTo(loadedTemplate.id)))
      .flatMap((loadedTemplate) => loadedTemplate.categories.map(getCategoryKey));
  }

  // No event to stop here any more: the panel's checkbox stops its own press before it reaches the
  // row, so a category is toggled once whether the checkbox or the row around it was pressed.
  async function handleTemplateCategoryToggled(
    selectedCategory: FeeScheduleTemplateCategory,
    template: FeeScheduleTemplate,
  ) {
    // Identity is by name, not id: a category added through the fee-schedule add panel is a value
    // snapshot with a freshly minted id (see the categorySource comment below), even when it started
    // as one of this template's own categories. Comparing by id would leave that category's checkbox
    // permanently unchecked here even though it is already in the proposal's fee schedule.
    const categoryWasSelected = selectedCategories.some((category) =>
      category.name?.isEqualTo(selectedCategory.name),
    );
    // One category off a template names a person just as a whole template does, so the same
    // question is asked for it.
    const staffing = categoryWasSelected
      ? undefined
      : await reconcileStaffing({
          categories: [selectedCategory],
          teamMembers: vendorTeamMembers,
          teamIsMasked: vendorTeamIsMasked,
        });
    if (staffing && !staffing.applied) return;
    const updatedCategories = categoryWasSelected
      ? selectedCategories.filter((category) => !category.name?.isEqualTo(selectedCategory.name))
      : [...selectedCategories, ...(staffing?.applied ? staffing.categories : [])];
    const updatedTemplateIds = getFeeScheduleTemplateIdsAfterCategoryToggled({
      selectedCategories: updatedCategories,
      selectedTemplateIds: selectedTemplateIds ?? [],
      template,
      toggledCategory: selectedCategory,
    });
    let updatedProposalBuilder = session.context?.viewingAsVendor
      ? proposalBuilder.setVendorFeeScheduleTemplateIds(updatedTemplateIds)
      : proposalBuilder.setClientFeeScheduleTemplateIds(updatedTemplateIds);
    if (staffing?.applied && staffing.membersToAddToTeam.length > 0) {
      updatedProposalBuilder = updatedProposalBuilder.setVendorTeam(
        withFeeScheduleStaffingSeated(currentVendorTeam ?? new VendorTeam({ members: [] }), staffing.membersToAddToTeam),
      );
    }
    updatedProposalBuilder = updatedProposalBuilder.setFeeSchedule(updatedCategories);

    onProposalBuilderUpdated(updatedProposalBuilder);
  }

  function handleCategoryRemoved(categoryName: FeeScheduleCategoryName) {
    const removedCategory = selectedCategories.find((category) => category.name?.isEqualTo(categoryName));
    const updatedCategories = selectedCategories.filter((category) => !category.name?.isEqualTo(categoryName));
    let updatedProposalBuilder = proposalBuilder.setFeeSchedule(updatedCategories);
    const currentTemplateIds = selectedTemplateIds ?? [];
    const removedCategoryTemplate = removedCategory
      ? templateFeeSchedules.find((template) =>
          template.categories.some((category) => category.id.isEqualTo(removedCategory.id)),
        )
      : undefined;

    if (removedCategory) {
      const updatedTemplateIds = removeSelectedFeeScheduleTemplateCategory({
        removedCategory,
        selectedCategories: updatedCategories,
        selectedTemplateIds: currentTemplateIds,
        template: removedCategoryTemplate,
      });
      updatedProposalBuilder = session.context?.viewingAsVendor
        ? updatedProposalBuilder.setVendorFeeScheduleTemplateIds(updatedTemplateIds)
        : updatedProposalBuilder.setClientFeeScheduleTemplateIds(updatedTemplateIds);
    }

    onProposalBuilderUpdated(updatedProposalBuilder);
  }

  // A revert restores a category's prior value through the SAME `setFeeSchedule` builder path any
  // other category edit uses (see FeeScheduleTemplatesTable's onEditCategory-driven callers), so it
  // flows to `queueProposalUpdate` as a `CollectionItemUpdated`/`CollectionItemAdded` op — never a raw
  // `FieldSet`.
  function handleCategoryReverted(category: FeeScheduleTemplateCategory) {
    const updatedCategories = selectedCategories.some((existing) => existing.id.isEqualTo(category.id))
      ? selectedCategories.map((existing) => (existing.id.isEqualTo(category.id) ? category : existing))
      : [...selectedCategories, category];
    onProposalBuilderUpdated(proposalBuilder.setFeeSchedule(updatedCategories));
  }

  function handleCreateTemplateClicked() {
    if (!selectedCategories.length) {
      enqueueSnackbar("Add at least one fee schedule category before creating a template.", { variant: "warning" });
      return;
    }

    setCreateTemplateName(getDefaultFeeScheduleTemplateName(proposalBuilder.currentSpec.workAgreement?.name?.value));
    setIsCreateTemplateDialogOpen(true);
  }

  function handleCreateTemplateDialogClose() {
    if (isCreatingTemplate) return;
    setIsCreateTemplateDialogOpen(false);
  }

  async function handleCreateTemplateConfirmed() {
    const templateName = createTemplateName.trim();
    if (!templateName || !selectedCategories.length || !session.user) return;

    try {
      setIsCreatingTemplate(true);
      const response = await createFeeScheduleTemplate({
        template: new FeeScheduleTemplate({
          name: templateName,
          tags: [],
          // The staffing travels now (#5237): these people are on this proposal's vendor team,
          // and a template that remembers them is the point of the feature.
          categories: [...selectedCategories],
          creator: session.user.toEntityMember(),
        }),
        // A proposal's fee schedule is a list of snapshots, not catalog
        // references. Some arrive with a client-minted id (typed in via the add
        // panel) and some retain the id of the template category they were
        // copied from, but even the latter are snapshots: their fee is
        // negotiated per proposal and may already have diverged from the catalog
        // row. Nothing on the objects distinguishes the two, so the whole array
        // is declared as values and the server promotes each one into the
        // catalog by normalized name.
        categorySource: "values",
      }).unwrap();
      const createdTemplate = FeeScheduleTemplate.fromJSON(response);
      if (!createdTemplate.id) throw new Error("Created fee schedule template is missing an ID");

      const nextTemplateIds = [...(selectedTemplateIds ?? []), createdTemplate.id];
      const updatedProposalBuilder = session.context?.viewingAsVendor
        ? proposalBuilder.setVendorFeeScheduleTemplateIds(nextTemplateIds)
        : proposalBuilder.setClientFeeScheduleTemplateIds(nextTemplateIds);

      // The new template arrives already checked, and it was built out of categories that were all
      // there beforehand — it added none of them but it supplies every one, which is what keeps them
      // from leaving with some other template the user later deselects.
      setTemplateContributions(
        withTemplateSelected({
          contributions: templateContributions,
          templateId: createdTemplate.id,
          suppliedItemKeys: selectedCategories.map(getCategoryKey),
          addedItemKeys: [],
        }),
      );
      onProposalBuilderUpdated(updatedProposalBuilder);
      enqueueSnackbar("Fee schedule template created", { variant: "success" });
      setIsCreateTemplateDialogOpen(false);
    } catch (error) {
      console.error("Failed to create fee schedule template from proposal", error);
      enqueueSnackbar("Failed to create fee schedule template. Please try again.", { variant: "error" });
    } finally {
      setIsCreatingTemplate(false);
    }
  }

  if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement?.feeSchedule)) {
    return maskedProposalFieldNotice(MaskedProposalField.FeeSchedule);
  }

  // The fee-schedule template query has no server-side name filter, so the panel narrows the page it
  // already holds: the rows handed to the selector are the templates in hand whose name matches what
  // was typed, compared case-insensitively.
  const templateSearchText = templateSearch.trim().toLocaleLowerCase();
  const templateRows: TemplateSelectorRow[] = [];
  for (const template of templateFeeSchedules) {
    // One bad template among good ones, which is not the failure the panel's empty area speaks for:
    // the row stays where that template would have been and says so, and the rest still draw.
    if (isFeeScheduleTemplateLoadIssue(template)) {
      if (!matchesFeeScheduleTemplateSearch(TEMPLATE_LOAD_FAILURE_NAME, templateSearchText)) continue;
      templateRows.push({
        disabled: true,
        disabledReason: "This template could not be loaded",
        id: template.id?.value ?? TEMPLATE_LOAD_FAILURE_NAME,
        name: TEMPLATE_LOAD_FAILURE_NAME,
        selected: false,
        onSelectedChanged: ignoreSelection,
      });
      continue;
    }
    if (!matchesFeeScheduleTemplateSearch(template.name, templateSearchText)) continue;

    const templateRowId = template.id?.value ?? template.name;
    const templateIsSelected = selectedTemplateIds?.some((id) => id.isEqualTo(template.id)) ?? false;
    templateRows.push({
      // The categories the row's expander reveals in place, which is where the list's Collapse put
      // them. By name, not id — see the identity comment in handleTemplateCategoryToggled.
      children: template.categories.map((category: FeeScheduleTemplateCategory) => ({
        content: (
          <CategoryRowText>
            <TemplateRowName>{category.name.value}</TemplateRowName>
            <CategoryRowDetail>
              {category.description?.value}
              <br />
              {category instanceof FeeScheduleCategoryMaskedCurrency ? "Masked" : category.feeString}
            </CategoryRowDetail>
          </CategoryRowText>
        ),
        id: `${templateRowId}:${getCategoryKey(category)}`,
        name: category.name.value,
        selected: selectedCategories.some((selectedCategory) =>
          selectedCategory.name?.isEqualTo(category.name),
        ),
        onSelectedChanged: () => handleTemplateCategoryToggled(category, template),
      })),
      content: (
        <>
          <TemplateRowName>{template.name}</TemplateRowName>
          {template.tags.map((tag: FeeScheduleTag) => (
            <FeeScheduleTagChip key={tag.value} size="small" label={tag.value} />
          ))}
        </>
      ),
      id: templateRowId,
      // Its categories are in the fee schedule without the template itself having been applied — the
      // rule the list's own checkbox carried, unchanged.
      indeterminate: !templateIsSelected && template.isUsingCategories(selectedCategories),
      name: template.name,
      selected: templateIsSelected,
      onSelectedChanged: () => handleTemplateSelectionToggled(template),
    });
  }

  return (
    <>
      <ListContainer container>
        {!disableEditing && (
          <>
            <ResizableTemplatePanel minimized={isDrawerOpen} selectedCount={selectedTemplateCount}>
              {/*
                One panel, drawn once. The selector brings its own compact accordion, so the second
                copy this tab used to render behind a media query — and the stale duplicate of every
                control in it — is gone; `compact` is the pane measurement, not the viewport's.

                No `tabs`: there is one list of fee schedule templates in the domain and no second
                source to offer, so the strip stays off rather than being given a single tab to draw.
                No `sorting` either — the panel holds every row it is showing, so the table sorts
                them itself.
              */}
              <TemplateSelector
                action={
                  <Tooltip
                    title={
                      selectedCategories.length
                        ? "Create a fee schedule template from these categories"
                        : "Add at least one category before creating a template"
                    }
                  >
                    <CreateTemplateActionWrapper>
                      <CreateTemplateAction
                        size="small"
                        startIcon={<SaveIcon />}
                        variant="outlined"
                        loading={isCreatingTemplate}
                        disabled={!selectedCategories.length || isCreatingTemplate}
                        onClick={handleCreateTemplateClicked}
                      >
                        Create Template
                      </CreateTemplateAction>
                    </CreateTemplateActionWrapper>
                  </Tooltip>
                }
                collapsedLabel="Fee Schedule Templates"
                compact={isMdDownDisplay}
                ownership={{
                  label: "fee schedule templates",
                  value: templateOwnershipScope,
                  onChange: setTemplateOwnershipScope,
                }}
                pagination={{
                  onChange: setTemplatePagination,
                  rowCount: paginatedTemplatesJSON?.headers.totalElements ?? 0,
                  value: templatePagination,
                }}
                rows={templateRows}
                search={{ onChange: setTemplateSearch, value: templateSearch }}
                selectedCount={selectedTemplateCount}
                status={{
                  isError: templatesQuery.isError,
                  isFetching: templatesQuery.isFetching,
                  isLoading: templatesQuery.isLoading,
                  onRetry: () => void templatesQuery.refetch(),
                }}
                subject="fee schedule templates"
              />
            </ResizableTemplatePanel>
            {!isMdDownDisplay && (
              <Grid>
                <Divider orientation="vertical" />
              </Grid>
            )}
          </>
        )}
        <FeeScheduleContent size="grow" sx={{ mt: 1 }}>
          {(issues?.length ?? 0) > 0 && (
            <IssueContainer
              active={issues?.some((issue) => issue.field.isEqualTo(activeReviewField)) ?? false}
            >
              {issues?.map((issue) => (
                <Typography
                  key={issue.field?.cacheKey}
                  color={issue.level === ProposalIssueLevel.Critical ? "error" : "warning"}
                  variant="body1"
                >
                  {issue.description}
                </Typography>
              ))}
            </IssueContainer>
          )}
          <CategorySelection
            selectedCategories={selectedCategories}
            hideFromTemplateButton={true}
            negotiationId={negotiationId}
            viewerScope={viewerScope}
            disableEditing={disableEditing}
            fieldLockTooltip={fieldLockTooltip}
            activeReviewField={activeReviewField}
            pendingChangeFields={pendingChangeFields}
            onCreateCategoryClicked={onCreateCategoryClicked}
            onExistingCategoryClicked={onAddExistingCategoryClicked}
            onEditCategory={onEditCategory}
            onCategoryRemoved={handleCategoryRemoved}
            onCategoryReverted={handleCategoryReverted}
          />
        </FeeScheduleContent>
      </ListContainer>
      <Dialog
        fullWidth
        maxWidth="xs"
        open={isCreateTemplateDialogOpen}
        onClose={handleCreateTemplateDialogClose}
      >
        <DialogTitle>Create Fee Schedule Template</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Template Name"
            margin="dense"
            value={createTemplateName}
            onChange={(event) => setCreateTemplateName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") void handleCreateTemplateConfirmed();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={isCreatingTemplate} onClick={handleCreateTemplateDialogClose}>
            Cancel
          </Button>
          <LoadingButton
            loading={isCreatingTemplate}
            disabled={!createTemplateName.trim()}
            onClick={handleCreateTemplateConfirmed}
          >
            Create
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** What the panel says in place of a template that could not be read. */
const TEMPLATE_LOAD_FAILURE_NAME = "Error loading template.";

/** The page size the tab has always asked the fee-schedule template endpoint for. */
const INITIAL_TEMPLATE_PAGINATION = { pageIndex: 0, pageSize: PaginationParameters.DEFAULT_PAGE_SIZE };

/** The row that failed to load cannot be applied, so pressing it is not an act with a result. */
function ignoreSelection() {
  return undefined;
}

/**
 * Whether a template belongs in the panel under what was typed.
 *
 * At module scope and by name only, because the filtering happens in hand: the fee-schedule template
 * endpoint takes no name filter, so the panel narrows the page it was given rather than asking again.
 */
function matchesFeeScheduleTemplateSearch(name: string, searchText: string): boolean {
  return searchText.length === 0 || name.toLocaleLowerCase().includes(searchText);
}

// By name, not id — see the identity comment in handleTemplateCategoryToggled. Name is therefore
// also the unit a template can be said to have contributed.
export function getCategoryKey(category: FeeScheduleTemplateCategory): string {
  return category.name?.value ?? "";
}

export function getDefaultFeeScheduleTemplateName(proposalName?: string | null): string {
  const trimmedName = proposalName?.trim();
  return trimmedName ? `${trimmedName} Fee Schedule` : "Proposal Fee Schedule";
}

export function removeSelectedFeeScheduleTemplateId(selectedTemplateIds: Guid[], removedCategoryId: Guid) {
  return selectedTemplateIds.filter((id) => !id.isEqualTo(removedCategoryId));
}

// `selectedTemplateIds` (persisted as clientFeeScheduleTemplateIds/vendorFeeScheduleTemplateIds) is a
// list of whole-TEMPLATE ids, used only to render each template's own checkbox as checked. A fee
// schedule CATEGORY's id must never appear in it: the two id spaces share no meaning, and doing so
// both corrupts the "N Templates" count derived from this array's length and (since a category id
// never matches template.id) leaves the row's own checkbox unable to reflect the true state. Whether
// a template belongs in this list is entirely a function of whether every one of its categories is
// currently selected — recomputed here from `selectedCategories`, never from the toggled/removed
// category's id.
function withTemplateFullSelectionState(
  selectedTemplateIds: Guid[],
  selectedCategories: FeeScheduleTemplateCategory[],
  template?: FeeScheduleTemplate,
): Guid[] {
  if (!template?.id) return uniqFeeScheduleTemplateIds(selectedTemplateIds);

  // By name, not id — see the identity comment in handleTemplateCategoryToggled.
  const templateIsFullySelected =
    template.categories.length > 0 &&
    template.categories.every((category) =>
      selectedCategories.some((selectedCategory) => selectedCategory.name?.isEqualTo(category.name)),
    );
  const idsWithoutTemplate = selectedTemplateIds.filter((id) => !id.isEqualTo(template.id));

  return uniqFeeScheduleTemplateIds(
    templateIsFullySelected ? [...idsWithoutTemplate, template.id] : idsWithoutTemplate,
  );
}

export function getFeeScheduleTemplateIdsAfterCategoryToggled(args: {
  selectedCategories: FeeScheduleTemplateCategory[];
  selectedTemplateIds: Guid[];
  template: FeeScheduleTemplate;
  toggledCategory: FeeScheduleTemplateCategory;
}) {
  const { selectedCategories, selectedTemplateIds, template, toggledCategory } = args;
  // Defensive cleanup: strips the toggled category's id in case it (or another category id) leaked
  // into this list before this fix, in addition to recomputing the template's own id below.
  const idsWithoutToggledCategory = removeSelectedFeeScheduleTemplateId(selectedTemplateIds, toggledCategory.id);

  return withTemplateFullSelectionState(idsWithoutToggledCategory, selectedCategories, template);
}

export function removeSelectedFeeScheduleTemplateCategory(args: {
  removedCategory: FeeScheduleTemplateCategory;
  selectedCategories: FeeScheduleTemplateCategory[];
  selectedTemplateIds: Guid[];
  template?: FeeScheduleTemplate;
}) {
  const { removedCategory, selectedCategories, selectedTemplateIds, template } = args;
  // Defensive cleanup: strips a category id that leaked into this list before this fix (e.g. on a
  // proposal saved by an older client build), in addition to recomputing the template's own id.
  const idsWithoutRemovedCategory = removeSelectedFeeScheduleTemplateId(selectedTemplateIds, removedCategory.id);

  return withTemplateFullSelectionState(idsWithoutRemovedCategory, selectedCategories, template);
}

function uniqFeeScheduleTemplateIds(templateIds: Guid[]) {
  return _.uniqBy(templateIds, (id) => id.value);
}
